// The editor's form model and its mapping to the Experiments v2 draft body.
// The form keeps param values as text with an explicit type, so "10" stays a
// string when the admin says string and becomes 10 when they say int. Key and
// weight rules mirror wcc4-backend experiment-v2-validation.ts; targeting,
// version and schedule rules are left to the server, whose 400 is shown as is.
import type {
  ExpV2DraftBody,
  ExpV2DraftUpdateBody,
  ExpV2Kind,
  ExpV2ParamValue,
  ExpV2PreRegistration,
  ExpV2Targeting,
  ExpV2Test,
} from '@/types/experimentsV2';
import { percentToBuckets } from './units';

export const TEST_KEY_PATTERN = /^[a-z][a-z0-9_]{0,19}$/;
/** A route param is a real test key (never a path fragment such as `..`). */
export const isTestKey = (key: string | undefined): key is string =>
  key !== undefined && TEST_KEY_PATTERN.test(key);
export const LAYER_KEY_PATTERN = /^[a-z][a-z0-9_]{0,29}$/;
export const VARIANT_KEY_PATTERN = /^[a-z0-9_]{1,20}$/;
export const PARAM_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;
export const PARAM_STRING_MAX = 200;
/** Admin routes beside ':key'; a test with one of these keys could not be reached. */
export const RESERVED_KEYS = ['layers', 'lists', 'players'];
export const CONTROL = 'control';
export const FLAG_VARIANT = 'on';
export const HOLDOUT_LAYER = 'holdout';

export type ParamType = 'bool' | 'int' | 'float' | 'string';

export interface ParamRow {
  key: string;
  type: ParamType;
  value: string;
}

export interface VariantForm {
  key: string;
  weightPercent: number;
  params: ParamRow[];
}

export interface PreRegistrationForm {
  hypothesis: string;
  primaryMetric: string;
  guardrails: string[];
  minDetectableEffect: string;
  plannedEndDate: string | null;
  customAnalysis: string;
}

export interface DraftForm {
  key: string;
  name: string;
  kind: ExpV2Kind;
  layerKey: string;
  variants: VariantForm[];
  targeting: ExpV2Targeting;
  requiredAppVersion: string;
  buildAndroid: string;
  buildIos: string;
  economy: boolean;
  rangePercent: number;
  startsAt: string | null;
  endsAt: string | null;
  pre: PreRegistrationForm;
}

const PRE_FIELDS = [
  'hypothesis',
  'primaryMetric',
  'guardrails',
  'minDetectableEffect',
  'plannedEndDate',
] as const;

export function emptyDraft(kind: ExpV2Kind): DraftForm {
  return {
    key: '',
    name: '',
    kind,
    layerKey: '',
    variants:
      kind === 'flag'
        ? [{ key: FLAG_VARIANT, weightPercent: 100, params: [] }]
        : [
            { key: CONTROL, weightPercent: 50, params: [] },
            { key: 'b', weightPercent: 50, params: [] },
          ],
    targeting: {},
    requiredAppVersion: '',
    buildAndroid: '',
    buildIos: '',
    economy: false,
    rangePercent: 100,
    startsAt: null,
    endsAt: null,
    pre: {
      hypothesis: '',
      primaryMetric: '',
      guardrails: [],
      minDetectableEffect: '',
      plannedEndDate: null,
      customAnalysis: '',
    },
  };
}

export function parseParam(row: ParamRow): { value?: ExpV2ParamValue; problem?: string } {
  const text = row.value;
  switch (row.type) {
    case 'bool':
      if (text === 'true') return { value: true };
      if (text === 'false') return { value: false };
      return { problem: `param "${row.key}" must be true or false` };
    case 'int':
      if (/^-?\d+$/.test(text.trim())) return { value: Number(text.trim()) };
      return { problem: `param "${row.key}" must be a whole number` };
    case 'float': {
      const n = text.trim() === '' ? Number.NaN : Number(text);
      if (Number.isFinite(n)) return { value: n };
      return { problem: `param "${row.key}" must be a number` };
    }
    default:
      if (text.length > PARAM_STRING_MAX) {
        return { problem: `param "${row.key}" is longer than ${PARAM_STRING_MAX} characters` };
      }
      return { value: text };
  }
}

const typeOf = (v: unknown): ParamType =>
  typeof v === 'boolean'
    ? 'bool'
    : typeof v === 'number'
      ? Number.isInteger(v)
        ? 'int'
        : 'float'
      : 'string';

/** Rows for stored params; `types` overrides the per-value guess (a float stored as 2). */
export function paramRowsOf(
  params: Record<string, unknown>,
  types: Record<string, ParamType> = {}
): ParamRow[] {
  return Object.entries(params).map(([key, v]) => ({
    key,
    type: types[key] ?? typeOf(v),
    value: String(v),
  }));
}

const wholeBuckets = (percent: number) => percentToBuckets(percent);

const isBlank = (s: string | null | undefined) => !s || s.trim() === '';

/** Variant, weight and param problems only (the editor shows them beside the variants). */
export function variantProblems(form: DraftForm): string[] {
  const problems: string[] = [];
  const keys = form.variants.map((v) => v.key);

  if (form.kind === 'flag') {
    const only = form.variants[0];
    if (form.variants.length !== 1 || only.key !== FLAG_VARIANT || only.weightPercent !== 100) {
      problems.push('A flag has exactly one variant "on" at 100%');
    }
  } else {
    if (form.variants.length < 2 || form.variants.length > 5) {
      problems.push('A test has 2 to 5 variants');
    }
    if (!keys.includes(CONTROL)) problems.push('One variant must be named "control"');
    let total = 0;
    for (const v of form.variants) {
      const buckets = wholeBuckets(v.weightPercent);
      if (buckets === null || buckets < 1 || buckets > 999) {
        problems.push(`Weight of "${v.key}" must be between 0.1% and 99.9% in steps of 0.1%`);
      } else {
        total += buckets;
      }
    }
    if (total !== 1000) problems.push(`Weights must sum to 100% (now ${total / 10}%)`);
  }

  for (const k of keys) {
    if (!VARIANT_KEY_PATTERN.test(k)) {
      problems.push(`Variant key "${k}" must be 1-20 of a-z, 0-9 and _`);
    }
  }
  if (new Set(keys).size !== keys.length) problems.push('Variant keys must be different');

  const paramKeys = new Set(form.variants.flatMap((v) => v.params.map((p) => p.key)));
  for (const pk of paramKeys) {
    if (!PARAM_KEY_PATTERN.test(pk)) {
      problems.push(
        `Param key "${pk}" must start with a-z and use only a-z, 0-9 and _ (40 at most)`
      );
    }
    const missing = form.variants.filter((v) => !v.params.some((p) => p.key === pk));
    if (missing.length > 0) {
      problems.push(
        `Param "${pk}" is missing from ${missing.map((v) => `"${v.key}"`).join(', ')}; every variant sets the same params`
      );
    }
  }
  for (const v of form.variants) {
    const seen = new Set<string>();
    for (const p of v.params) {
      if (seen.has(p.key)) problems.push(`Param "${p.key}" appears twice in "${v.key}"`);
      seen.add(p.key);
      const parsed = parseParam(p);
      if (parsed.problem) problems.push(`${parsed.problem} in "${v.key}"`);
    }
  }
  return problems;
}

/** Every problem the editor can find before saving; [] when the draft can be sent. */
export function draftProblems(form: DraftForm): string[] {
  const problems: string[] = [];
  if (!TEST_KEY_PATTERN.test(form.key)) {
    problems.push('Key must start with a-z and use only a-z, 0-9 and _ (20 at most)');
  } else if (RESERVED_KEYS.includes(form.key)) {
    problems.push(`Key "${form.key}" is reserved`);
  }
  if (isBlank(form.name) || form.name.length > 100) problems.push('Name is required (100 at most)');
  if (isBlank(form.layerKey)) problems.push('Pick a layer');
  else if (form.layerKey === HOLDOUT_LAYER) problems.push('Tests cannot go in the holdout layer');

  problems.push(...variantProblems(form));

  const range = wholeBuckets(form.rangePercent);
  if (range === null || range < 1) {
    problems.push('Reserved % must be between 0.1% and 100% in steps of 0.1%');
  }
  for (const [label, text] of [
    ['Android', form.buildAndroid],
    ['iOS', form.buildIos],
  ] as const) {
    if (!isBlank(text) && !/^[1-9]\d*$/.test(text.trim())) {
      problems.push(`${label} build number must be a whole number above 0`);
    }
  }
  return problems;
}

/** Pre-registration fields still missing; Start stays disabled until this is []. */
export function preRegistrationMissing(form: DraftForm): string[] {
  if (form.kind !== 'experiment') return [];
  const pre = form.pre;
  return PRE_FIELDS.filter((field) => {
    switch (field) {
      case 'guardrails':
        return pre.guardrails.length === 0 || pre.guardrails.some(isBlank);
      case 'minDetectableEffect': {
        const n = isBlank(pre.minDetectableEffect) ? Number.NaN : Number(pre.minDetectableEffect);
        return !Number.isFinite(n) || n <= 0;
      }
      case 'plannedEndDate':
        return !pre.plannedEndDate || Number.isNaN(new Date(pre.plannedEndDate).getTime());
      default:
        return isBlank(pre[field]);
    }
  });
}

const cleanList = (list?: string[]) => {
  const out = (list ?? []).map((s) => s.trim()).filter((s) => s !== '');
  return out.length > 0 ? out : undefined;
};

const cleanRange = (r?: { min?: number; max?: number }) => {
  if (!r) return undefined;
  const out: { min?: number; max?: number } = {};
  if (typeof r.min === 'number' && Number.isFinite(r.min)) out.min = r.min;
  if (typeof r.max === 'number' && Number.isFinite(r.max)) out.max = r.max;
  return Object.keys(out).length > 0 ? out : undefined;
};

/** Targeting without empty lists, blank strings or empty ranges; null when nothing is left. */
export function cleanTargeting(t: ExpV2Targeting): ExpV2Targeting | null {
  const out: ExpV2Targeting = {};
  const include = cleanList(t.countries?.include);
  const exclude = cleanList(t.countries?.exclude);
  if (include || exclude) {
    out.countries = {};
    if (include) out.countries.include = include;
    if (exclude) out.countries.exclude = exclude;
  }
  const platforms = cleanList(t.platforms);
  if (platforms) out.platforms = platforms;
  if (!isBlank(t.minAppVersion)) out.minAppVersion = t.minAppVersion!.trim();
  if (t.payer) out.payer = t.payer;
  const days = cleanRange(t.daysSinceInstall);
  if (days) out.daysSinceInstall = days;
  const level = cleanRange(t.level);
  if (level) out.level = level;
  if (!isBlank(t.userListId)) out.userListId = t.userListId!.trim();
  return Object.keys(out).length > 0 ? out : null;
}

function preRegistrationBody(form: DraftForm): Partial<ExpV2PreRegistration> | undefined {
  if (form.kind !== 'experiment') return undefined;
  const pre = form.pre;
  const out: Partial<ExpV2PreRegistration> = {};
  if (!isBlank(pre.hypothesis)) out.hypothesis = pre.hypothesis;
  if (!isBlank(pre.primaryMetric)) out.primaryMetric = pre.primaryMetric;
  if (pre.guardrails.length > 0) out.guardrails = pre.guardrails;
  if (!isBlank(pre.minDetectableEffect)) out.minDetectableEffect = Number(pre.minDetectableEffect);
  if (pre.plannedEndDate) out.plannedEndDate = pre.plannedEndDate;
  if (!isBlank(pre.customAnalysis)) out.customAnalysis = pre.customAnalysis;
  return Object.keys(out).length > 0 ? out : undefined;
}

function typedParams(rows: ParamRow[]): Record<string, ExpV2ParamValue> {
  const out: Record<string, ExpV2ParamValue> = {};
  for (const row of rows) {
    const { value } = parseParam(row);
    if (value !== undefined) out[row.key] = value;
  }
  return out;
}

export function toCreateBody(form: DraftForm): ExpV2DraftBody {
  const builds: { android?: number; ios?: number } = {};
  if (!isBlank(form.buildAndroid)) builds.android = Number(form.buildAndroid.trim());
  if (!isBlank(form.buildIos)) builds.ios = Number(form.buildIos.trim());
  const body: ExpV2DraftBody = {
    key: form.key,
    name: form.name.trim(),
    kind: form.kind,
    layerKey: form.layerKey,
    variants: form.variants.map((v) => ({
      key: v.key,
      weight: Math.round(v.weightPercent * 10),
      params: typedParams(v.params),
    })),
    targeting: cleanTargeting(form.targeting),
    requiredAppVersion: isBlank(form.requiredAppVersion) ? null : form.requiredAppVersion.trim(),
    requiredBuildNumbers: Object.keys(builds).length > 0 ? builds : null,
    economy: form.economy,
    rangeSize: percentToBuckets(form.rangePercent) ?? 0,
    startsAt: form.startsAt,
    endsAt: form.endsAt,
  };
  const pre = preRegistrationBody(form);
  if (pre) body.preRegistration = pre;
  return body;
}

export function toUpdateBody(form: DraftForm): ExpV2DraftUpdateBody {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { key, kind, ...rest } = toCreateBody(form);
  return rest;
}

/** A param is a float when any variant holds a fraction for it. */
function paramTypes(test: ExpV2Test): Record<string, ParamType> {
  const types: Record<string, ParamType> = {};
  for (const v of test.variants) {
    for (const [k, value] of Object.entries(v.params)) {
      const t = typeOf(value);
      if (types[k] === undefined || (types[k] === 'int' && t === 'float')) types[k] = t;
    }
  }
  return types;
}

export function fromTest(test: ExpV2Test): DraftForm {
  const types = paramTypes(test);
  const pre = test.preRegistration;
  return {
    key: test.key,
    name: test.name,
    kind: test.kind,
    layerKey: test.layerKey,
    variants: test.variants.map((v) => ({
      key: v.key,
      weightPercent: v.weight / 10,
      params: paramRowsOf(v.params, types),
    })),
    targeting: test.targeting ?? {},
    requiredAppVersion: test.requiredAppVersion ?? '',
    buildAndroid:
      test.requiredBuildNumbers?.android !== undefined
        ? String(test.requiredBuildNumbers.android)
        : '',
    buildIos:
      test.requiredBuildNumbers?.ios !== undefined ? String(test.requiredBuildNumbers.ios) : '',
    economy: test.economy,
    rangePercent: test.rangeSize / 10,
    startsAt: test.startsAt ?? null,
    endsAt: test.endsAt ?? null,
    pre: {
      hypothesis: pre?.hypothesis ?? '',
      primaryMetric: pre?.primaryMetric ?? '',
      guardrails: pre?.guardrails ?? [],
      minDetectableEffect:
        pre?.minDetectableEffect !== undefined ? String(pre.minDetectableEffect) : '',
      plannedEndDate: pre?.plannedEndDate ?? null,
      customAnalysis: pre?.customAnalysis ?? '',
    },
  };
}
